import { useCallback, useEffect, useRef, useState } from 'react';
import { ToastOptions } from './toast-context';

const DEFAULT_DURATION = 10000;

interface ToastState extends Required<ToastOptions> {
  key: number;
};

interface UseToastReturn {
  toast: ToastState | null;
  visible: boolean;
  showToast: (options: ToastOptions) => void;
  dismissToast: () => void;
  onExited: () => void;
}

export function useToast(): UseToastReturn {
  const [toast, setToast] = useState<ToastState | null>(null);
  const [visible, setVisible] = useState(false);
  const autoTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingToast = useRef<ToastState | null>(null);

  const clearTimers = useCallback(() => {
    if (autoTimer.current) {
      clearTimeout(autoTimer.current);
      autoTimer.current = null;
    }
  }, []);

  const dismissToast = useCallback(() => {
    clearTimers();
    setVisible(false);
  }, [clearTimers]);

  const showToast = useCallback(
    ({ message, type = 'info', duration = DEFAULT_DURATION, persistent = false }: ToastOptions) => {
      const next: ToastState = { message, type, duration, persistent, key: Date.now() };

      if (visible) {
        pendingToast.current = next;
        dismissToast();
      } else {
        setToast(next);
        setVisible(true);
      }
    },
    [visible, dismissToast]
  );

  const onExited = useCallback(() => {
    if (pendingToast.current) {
      const next = pendingToast.current;
      pendingToast.current = null;
      setToast(next);
      setVisible(true);
    }
  }, []);

  useEffect(() => {
    if (!visible || !toast || toast.persistent) return;

    autoTimer.current = setTimeout(() => {
      setVisible(false);
    }, toast.duration);

    return clearTimers;
  }, [visible, toast, clearTimers]);

  return { toast, visible, showToast, dismissToast, onExited };
}
